/**
 * Ads tags for the shop: Meta Pixel and the Google tag (gtag.js) — OFF until configured, and
 * 🔴 nothing loads before the visitor presses Accept (POPIA). Server half: worker/src/shop-ads.ts.
 *
 *   configureAds(config.ads)  — once /shop/config lands; loads the tags if consent was given before
 *   setAdsConsent("granted" | "denied") — the banner's buttons (ConsentBanner.tsx)
 *   adsPageView()             — on every in-shop navigation after the first
 *   adsEvent(name, data)      — standard events, for track.ts to call (no-op when off)
 *   adsPurchase(p, email)     — the order page, once per order (event_id = order reference)
 *
 * The choice is the first-party cookie `zs_ads_consent` (12 months) so the checkout request carries
 * it to the server, which records browser identifiers only when it says "granted".
 *
 * ⚠️ No imports and no import.meta: scripts/verify-ads-tracking.ts runs this file in Node with a
 * fake document.
 */

export interface AdsConfig {
  metaPixelId: string | null;
  googleAdsId: string | null;
  googleAdsPurchaseLabel: string | null;
  ga4Id: string | null;
}
export type AdsConsent = "granted" | "denied";
export type AdsEventName = "ViewContent" | "Search" | "AddToCart" | "InitiateCheckout";
export interface AdsEventData {
  /** The feed's item ids (shop-feed.ts `feedItemId`), so catalogue ads match. */
  contentIds?: string[];
  valueCents?: number;
  quantity?: number;
  /** Search only: what was typed. */
  query?: string;
  contentName?: string;
}
export interface AdsPurchase {
  eventId: string;
  valueCents: number;
  currency: "ZAR";
  contentIds: string[];
  quantities?: number[];
}

export const CONSENT_COOKIE = "zs_ads_consent";
const CONSENT_MAX_AGE = 365 * 24 * 3600;
/** Fired to reopen the banner from the footer's "Cookie settings". */
export const CONSENT_OPEN_EVENT = "zs:ads-consent-open";
const FIRED_KEY = "zs_ads_purchases";

type Fn = (...args: unknown[]) => void;
interface AdsWindow {
  fbq?: Fn & { callMethod?: Fn; queue?: unknown[]; loaded?: boolean; version?: string; push?: Fn };
  _fbq?: unknown;
  dataLayer?: unknown[];
  gtag?: Fn;
}

let config: AdsConfig | null = null;
let loaded = false;
const firedThisPage = new Set<string>();

/** The few DOM members used — typed here so the worker's tsc (no DOM lib) can check the verify script. */
interface MiniDoc {
  cookie: string;
  title?: string;
  createElement(tag: "script"): { async: boolean; src: string; setAttribute(k: string, v: string): void };
  head: { appendChild(node: never): unknown };
}
interface MiniStorage {
  getItem(k: string): string | null;
  setItem(k: string, v: string): void;
}
type Globals = { document?: MiniDoc; location?: { protocol: string; href: string }; localStorage?: MiniStorage };
const w = (): AdsWindow => globalThis as unknown as AdsWindow;
const doc = (): MiniDoc | null => (globalThis as Globals).document ?? null;
const loc = () => (globalThis as Globals).location ?? null;
const storage = (): MiniStorage | null => (globalThis as Globals).localStorage ?? null;

export function hasAdsIds(c: AdsConfig | null | undefined): c is AdsConfig {
  return Boolean(c && (c.metaPixelId || c.googleAdsId || c.ga4Id));
}

export function readAdsConsent(): AdsConsent | null {
  const d = doc();
  if (!d) return null;
  try {
    for (const part of d.cookie.split(";")) {
      const [k, v] = part.split("=");
      if (k?.trim() === CONSENT_COOKIE) return v?.trim() === "granted" ? "granted" : v?.trim() === "denied" ? "denied" : null;
    }
  } catch {
    /* cookies blocked: treated as no answer yet */
  }
  return null;
}

/** The banner shows only while some ad id is configured and the visitor has not answered (or asked to change it). */
export const bannerVisible = (c: AdsConfig | null | undefined, consent: AdsConsent | null, reopened: boolean): boolean =>
  hasAdsIds(c) && (consent === null || reopened);

/** True when tags may run: configured AND accepted. */
export const adsActive = (): boolean => hasAdsIds(config) && readAdsConsent() === "granted";

export function configureAds(c: AdsConfig | null | undefined): void {
  config = hasAdsIds(c) ? c : null;
  if (adsActive()) loadAdsTags();
}

export function setAdsConsent(v: AdsConsent): void {
  const d = doc();
  if (d) {
    try {
      const secure = loc()?.protocol === "https:" ? "; Secure" : "";
      d.cookie = `${CONSENT_COOKIE}=${v}; Max-Age=${CONSENT_MAX_AGE}; Path=/; SameSite=Lax${secure}`;
    } catch {
      /* blocked cookies: nothing loads, which is the safe side */
    }
  }
  if (v === "granted") {
    if (loaded) {
      w().fbq?.("consent", "grant");
      w().gtag?.("consent", "update", { ad_storage: "granted", ad_user_data: "granted", ad_personalization: "granted", analytics_storage: "granted" });
    } else if (hasAdsIds(config)) loadAdsTags();
  } else if (loaded) {
    /** A script cannot be unloaded; it is told to stop. A reload drops it altogether. */
    w().fbq?.("consent", "revoke");
    w().gtag?.("consent", "update", { ad_storage: "denied", ad_user_data: "denied", ad_personalization: "denied", analytics_storage: "denied" });
  }
}

function addScript(src: string): void {
  const d = doc();
  if (!d) return;
  const s = d.createElement("script");
  s.async = true;
  s.src = src;
  s.setAttribute("data-ads", "1");
  d.head.appendChild(s as never);
}

/** Meta's and Google's standard loader snippets, inline. Called only after consent. */
export function loadAdsTags(): void {
  if (loaded || !config || readAdsConsent() !== "granted") return;
  loaded = true;
  const win = w();
  if (config.metaPixelId) {
    if (!win.fbq) {
      const n = function (...args: unknown[]) {
        if (n.callMethod) n.callMethod(...args);
        else n.queue!.push(args);
      } as NonNullable<AdsWindow["fbq"]>;
      n.push = n;
      n.loaded = true;
      n.version = "2.0";
      n.queue = [];
      win.fbq = n;
      if (!win._fbq) win._fbq = n;
      addScript("https://connect.facebook.net/en_US/fbevents.js");
    }
    win.fbq!("init", config.metaPixelId);
    win.fbq!("track", "PageView");
  }
  const gid = config.googleAdsId ?? config.ga4Id;
  if (gid) {
    win.dataLayer = win.dataLayer ?? [];
    win.gtag = function gtag() {
      // gtag.js reads the `arguments` object itself, not an array.
      // eslint-disable-next-line prefer-rest-params
      win.dataLayer!.push(arguments);
    };
    win.gtag("consent", "default", { ad_storage: "granted", ad_user_data: "granted", ad_personalization: "granted", analytics_storage: "granted" });
    win.gtag("js", new Date());
    if (config.googleAdsId) win.gtag("config", config.googleAdsId, { allow_enhanced_conversions: true });
    if (config.ga4Id) win.gtag("config", config.ga4Id);
    addScript(`https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(gid)}`);
  }
}

/** An in-shop navigation (the first page view is sent by loadAdsTags / gtag config). */
export function adsPageView(): void {
  if (!adsActive() || !loaded) return;
  w().fbq?.("track", "PageView");
  const l = loc();
  if (w().gtag && l) w().gtag!("event", "page_view", { page_location: l.href, page_title: doc()?.title });
}

const GOOGLE_NAMES: Record<AdsEventName, string> = { ViewContent: "view_item", Search: "search", AddToCart: "add_to_cart", InitiateCheckout: "begin_checkout" };

/** Standard events — consent-aware, a no-op when tracking is off. For track.ts. */
export function adsEvent(name: AdsEventName, data: AdsEventData = {}): void {
  if (!adsActive()) return;
  if (!loaded) loadAdsTags();
  const value = data.valueCents !== undefined ? Math.round(data.valueCents) / 100 : undefined;
  const ids = data.contentIds ?? [];
  w().fbq?.("track", name, {
    ...(ids.length ? { content_ids: ids, content_type: "product" } : {}),
    ...(value !== undefined ? { value, currency: "ZAR" } : {}),
    ...(data.query ? { search_string: data.query } : {}),
    ...(data.contentName ? { content_name: data.contentName } : {}),
  });
  w().gtag?.("event", GOOGLE_NAMES[name], {
    ...(value !== undefined ? { value, currency: "ZAR" } : {}),
    ...(data.query ? { search_term: data.query } : {}),
    ...(ids.length ? { items: ids.map((id) => ({ item_id: id, quantity: data.quantity ?? 1 })) } : {}),
  });
}

const TRACK_TO_ADS: Record<string, AdsEventName> = { view_item: "ViewContent", search: "Search", add_to_cart: "AddToCart", begin_checkout: "InitiateCheckout" };

/** The `onTrack` listener (store-main.tsx): shop events → ad-tag standard events. adsEvent itself gates on ids + consent. */
export function trackToAds(name: string, data: Record<string, unknown>): void {
  const ev = TRACK_TO_ADS[name];
  if (!ev) return;
  const ids = Array.isArray(data.contentIds) ? data.contentIds.filter((x): x is string => typeof x === "string" && x !== "") : [];
  adsEvent(ev, {
    ...(ids.length ? { contentIds: ids } : {}),
    ...(typeof data.valueCents === "number" ? { valueCents: data.valueCents } : {}),
    ...(typeof data.quantity === "number" ? { quantity: data.quantity } : {}),
    ...(typeof data.query === "string" ? { query: data.query } : {}),
    ...(typeof data.contentName === "string" ? { contentName: data.contentName } : {}),
  });
}

function alreadyFired(id: string): boolean {
  if (firedThisPage.has(id)) return true;
  try {
    const list = JSON.parse(storage()?.getItem(FIRED_KEY) ?? "[]") as string[];
    return Array.isArray(list) && list.includes(id);
  } catch {
    return false;
  }
}
function markFired(id: string): void {
  firedThisPage.add(id);
  try {
    const list = JSON.parse(storage()?.getItem(FIRED_KEY) ?? "[]") as string[];
    storage()?.setItem(FIRED_KEY, JSON.stringify([...(Array.isArray(list) ? list : []), id].slice(-30)));
  } catch {
    /* storage blocked: the in-page set still stops a double fire; Meta dedupes on eventID */
  }
}

async function sha256Hex(s: string): Promise<string | null> {
  try {
    const d = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(s));
    return [...new Uint8Array(d)].map((b) => b.toString(16).padStart(2, "0")).join("");
  } catch {
    return null;
  }
}

/**
 * The order page, once paid: Purchase ONCE per order. `eventId` = the order reference — the
 * same event_id the server's Conversions API event carries, so Meta counts one purchase.
 * Google Ads gets an Enhanced Conversion (SHA-256 of the normalised email; never plaintext).
 */
export async function adsPurchase(p: AdsPurchase, email: string | null): Promise<boolean> {
  if (!adsActive() || alreadyFired(p.eventId)) return false;
  markFired(p.eventId);
  if (!loaded) loadAdsTags();
  const value = Math.round(p.valueCents) / 100;
  const c = config!;
  w().fbq?.("track", "Purchase", { value, currency: p.currency, content_ids: p.contentIds, content_type: "product", num_items: (p.quantities ?? []).reduce((a, q) => a + q, 0) || p.contentIds.length }, { eventID: p.eventId });
  const g = w().gtag;
  if (g) {
    const hashed = email ? await sha256Hex(email.trim().toLowerCase()) : null;
    if (hashed) g("set", "user_data", { sha256_email_address: hashed });
    if (c.googleAdsId && c.googleAdsPurchaseLabel) {
      g("event", "conversion", { send_to: `${c.googleAdsId}/${c.googleAdsPurchaseLabel}`, value, currency: p.currency, transaction_id: p.eventId });
    }
    if (c.ga4Id) {
      g("event", "purchase", { transaction_id: p.eventId, value, currency: p.currency, items: p.contentIds.map((id, i) => ({ item_id: id, quantity: p.quantities?.[i] ?? 1 })) });
    }
  }
  return true;
}
